import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { apiUrl } from '../lib/api';
import { getSolicitacoes, BROADCAST_SOLICITACOES_FORNECEDOR } from '../lib/solicitacoesFornecedorStorage';
import { getGuias, getAgencias } from '../lib/guiasAgenciasStorage';
import { useAdminCatalog } from '../contexts/AdminCatalogContext';
import { resolveExcursaoStatus } from '../../../shared/lib/excursaoStorage';
import DashboardHomeExperiencesLayoutTab from './DashboardHomeExperiencesLayoutTab';

interface ParceiroCadastroRow {
  id: string;
  nome: string;
  email: string;
  tipo: string;
  status: string;
  origem: string;
}

function loadParceirosCadastroRows(): ParceiroCadastroRow[] {
  const out: ParceiroCadastroRow[] = [];
  for (const s of getSolicitacoes()) {
    if (s.status === 'aprovado') continue;
    out.push({
      id: `sol-${s.id}`,
      nome: s.nome,
      email: s.email,
      tipo: s.roles.join(', '),
      status: s.status === 'pendente' ? 'Em análise' : 'Recusado',
      origem: 'Solicitação (app)',
    });
  }
  for (const g of getGuias()) {
    if (g.status !== 'pendente') continue;
    out.push({
      id: `guia-${g.id}`,
      nome: g.nome,
      email: g.email,
      tipo: (g.perfis?.length ? g.perfis.join(', ') : 'Guia') || 'Guia',
      status: 'Em análise',
      origem: 'Cadastro manual',
    });
  }
  for (const a of getAgencias()) {
    if (a.status !== 'pendente') continue;
    out.push({
      id: `ag-${a.id}`,
      nome: a.razaoSocial,
      email: a.email ?? '—',
      tipo: 'Agência',
      status: 'Em análise',
      origem: 'Cadastro manual',
    });
  }
  return out.sort((x, y) => x.nome.localeCompare(y.nome, 'pt-BR'));
}

type StatusReserva = 'em formação' | 'confirmada' | 'cancelada' | 'concluída';
type TipoReserva = 'Excursão' | 'Privada';
type FormaPagamento = 'cartão' | 'pix' | '—';

interface ReservaRow {
  id: string;
  experiencia: string;
  guia: string;
  arrecadado: number;
  valorGuia: number;
  hora: string;
  data: string;
  tipo: TipoReserva;
  inscricoes: number;
  vagas: number;
  status: StatusReserva;
  formaPagamento: FormaPagamento;
  participantes?: string[];
}

interface ExcursaoApi {
  id: string;
  date: string;
  status: string;
  city: string;
  maxPessoas: number;
  quorumMinimo: number;
  valorPorPessoa: number;
  inscritos: number;
  horarioSaida: string;
  guiaId?: string;
  title?: string;
  experiences?: number[];
}

export default function DashboardPage() {
  const { catalogVersion } = useAdminCatalog();
  const [dashTab, setDashTab] = useState<'resumo' | 'homeExp'>('resumo');
  const [reservas, setReservas] = useState<ReservaRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [parceirosCadastro, setParceirosCadastro] = useState<ParceiroCadastroRow[]>([]);
  const [kpis, setKpis] = useState({
    novasReservas: 0,
    reservasSemGuia: 0,
    passosConfirmados: 0,
    emFormacao: 0,
  });

  const refreshParceirosCadastro = () => {
    setParceirosCadastro(loadParceirosCadastroRows());
  };

  useEffect(() => {
    refreshParceirosCadastro();
    let ch: BroadcastChannel | null = null;
    try {
      ch = new BroadcastChannel(BROADCAST_SOLICITACOES_FORNECEDOR);
      ch.onmessage = () => refreshParceirosCadastro();
    } catch {
      /* ignore */
    }
    const onFocus = () => refreshParceirosCadastro();
    window.addEventListener('focus', onFocus);
    return () => {
      ch?.close();
      window.removeEventListener('focus', onFocus);
    };
  }, [catalogVersion]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const res = await fetch(apiUrl('/api/excursoes'));
        const list: ExcursaoApi[] = res.ok ? await res.json() : [];
        if (cancelled) return;

        const rows: ReservaRow[] = (list || []).map((e) => {
          const valorPorPessoa = Number(e.valorPorPessoa) || 0;
          const inscritos = Number(e.inscritos) || 0;
          const arrecadado = valorPorPessoa * inscritos;
          const valorGuia = Math.round(arrecadado * 0.7); // exemplo: 70% para o guia
          const [hora = '09:00'] = (e.horarioSaida || '').split(':');
          const dataStr = e.date || '';
          const d = dataStr.split('-');
          const dataFormatada = d.length === 3 ? `${d[2]}/${d[1]}/${d[0]}` : dataStr;

          return {
            id: e.id,
            experiencia: e.title || e.city || 'Experiência',
            guia: e.guiaId || '—',
            arrecadado,
            valorGuia,
            hora: hora.length === 1 ? `0${hora}:00` : `${hora}:00`,
            data: dataFormatada,
            tipo: 'Excursão' as TipoReserva,
            inscricoes: inscritos,
            vagas: Number(e.maxPessoas) || 0,
            status: resolveExcursaoStatus({
              status:
                e.status === 'cancelada' || e.status === 'cancelado'
                  ? 'cancelada'
                  : 'em formação',
              date: e.date || '',
              quorumMinimo: Number(e.quorumMinimo) || 0,
              inscritos,
            }) as StatusReserva,
            formaPagamento: '—' as FormaPagamento,
          };
        });

        setReservas(rows);

        const emFormacao = rows.filter((r) => r.status === 'em formação').length;
        const confirmadas = rows.filter((r) => r.status === 'confirmada').length;
        setKpis({
          novasReservas: rows.length,
          reservasSemGuia: rows.filter((r) => r.guia === '—').length,
          passosConfirmados: confirmadas,
          emFormacao,
        });
      } catch {
        if (!cancelled) setReservas([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, []);

  const statusConfig: Record<StatusReserva, { bg: string; text: string; label: string }> = {
    'em formação': { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Em formação' },
    'confirmada': { bg: 'bg-green-100', text: 'text-green-800', label: 'Confirmado' },
    'cancelada': { bg: 'bg-red-100', text: 'text-red-800', label: 'Cancelado' },
    'concluída': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Concluída' },
  };

  return (
    <AdminLayout>
      <div className="bg-white min-h-screen">
        <div className="sticky top-0 z-20 px-6 py-6 border-b border-gray-200 bg-white shadow-sm">
          <nav className="text-sm text-gray-500 mb-1">
            Home &gt; Dashboard
          </nav>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <div className="mt-4 flex flex-wrap gap-2 border-b border-gray-200 -mb-px" role="tablist" aria-label="Secções do dashboard">
            <button
              type="button"
              role="tab"
              aria-selected={dashTab === 'resumo'}
              onClick={() => setDashTab('resumo')}
              className={`px-4 py-2.5 text-sm font-semibold rounded-t-lg border border-b-0 transition-colors ${
                dashTab === 'resumo'
                  ? 'bg-white border-gray-200 text-orange-600 border-b-white relative z-10 mb-[-1px]'
                  : 'bg-gray-50 border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              Resumo
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={dashTab === 'homeExp'}
              onClick={() => setDashTab('homeExp')}
              className={`px-4 py-2.5 text-sm font-semibold rounded-t-lg border border-b-0 transition-colors ${
                dashTab === 'homeExp'
                  ? 'bg-white border-gray-200 text-orange-600 border-b-white relative z-10 mb-[-1px]'
                  : 'bg-gray-50 border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              Home (Experiências)
            </button>
          </div>
        </div>

        {dashTab === 'homeExp' ? (
          <DashboardHomeExperiencesLayoutTab />
        ) : (
          <>
        {/* KPI cards */}
        <div className="px-6 py-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <p className="text-sm font-medium text-gray-500">Novas reservas</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{kpis.novasReservas}</p>
            <p className="text-xs text-green-600 mt-1">Reservas no período</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <p className="text-sm font-medium text-gray-500">Reservas sem guia</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{kpis.reservasSemGuia}</p>
            <p className="text-xs text-gray-500 mt-1">A designar</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <p className="text-sm font-medium text-gray-500">Passos confirmados</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{kpis.passosConfirmados}</p>
            <p className="text-xs text-gray-500 mt-1">Confirmadas</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <p className="text-sm font-medium text-gray-500">Em formação</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{kpis.emFormacao}</p>
            <p className="text-xs text-amber-600 mt-1">Aguardando</p>
          </div>
        </div>

        {/* Cadastro de parceiros — mesmos status que Parceiros */}
        <div className="px-6 pb-8">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h2 className="text-lg font-bold text-gray-800">Cadastro de parceiros</h2>
            <Link
              to="/guias"
              className="inline-flex px-4 py-2 rounded-lg bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 shadow-sm transition-colors"
            >
              Abrir Parceiros
            </Link>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Solicitações do app e cadastros manuais <strong>em análise</strong> ou <strong>recusados</strong>. Aprovados aparecem como <strong>Ativo</strong> na lista completa em Parceiros.
          </p>
          {parceirosCadastro.length === 0 ? (
            <div className="bg-gray-50 rounded-xl border border-gray-200 p-6 text-center text-gray-500 text-sm mb-6">
              Nenhuma solicitação pendente ou recusada. Todos os parceiros ativos estão em <Link to="/guias" className="text-orange-600 font-medium hover:underline">Parceiros</Link>.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm mb-8">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-slate-700 text-white">
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Nome</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">E-mail</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Tipo</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Origem</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {parceirosCadastro.map((row, idx) => {
                    const emAnalise = row.status === 'Em análise';
                    return (
                      <tr key={row.id} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} border-b border-gray-100`}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.nome}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{row.email}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{row.tipo}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{row.origem}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                              emAnalise ? 'bg-amber-100 text-amber-900' : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {row.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Tabela Próximas Experiências / Reservas */}
        <div className="px-6 pb-8">
          <h2 className="text-lg font-bold text-gray-800 mb-3">Reservas e excursões</h2>
          <p className="text-sm text-gray-500 mb-4">
            Resumo geral: valor arrecadado, valor para o guia, participantes, data, status e forma de pagamento (cartão, PIX).
          </p>

          {loading ? (
            <div className="text-gray-500 py-8 text-center">Carregando...</div>
          ) : reservas.length === 0 ? (
            <div className="bg-gray-50 rounded-xl border border-gray-200 p-8 text-center text-gray-500">
              Nenhuma reserva ou excursão no momento.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-orange-500 text-white">
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Experiência</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Guia</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Arrecadado</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Valor guia</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Hora</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Data</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Tipo</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Inscrições / Vagas</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Pagamento</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {reservas.map((r, idx) => {
                    const sc = statusConfig[r.status];
                    return (
                      <tr key={r.id} className={`${['bg-white', 'bg-gray-50', 'bg-orange-50'][idx % 3]} border-b border-gray-100 hover:opacity-90 transition-opacity`}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{r.experiencia}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{r.guia}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">R$ {r.arrecadado.toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">R$ {r.valorGuia.toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{r.hora}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{r.data}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{r.tipo}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{r.inscricoes}/{r.vagas}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{r.formaPagamento}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${sc.bg} ${sc.text}`}>
                            {sc.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
